'use client'

import { QueryClientProvider } from '@tanstack/react-query'
import * as React from 'react'
import { create } from 'zustand'
import { queryClient } from '~/lib/react-query-client'
import { Toaster } from '~/sotano-kit/primitives/toast'
import { NuqsAdapter } from 'nuqs/adapters/next/app'
import { TooltipProvider } from '~/sotano-kit/primitives/tooltip'
import { UserStoreSetUp, useUserStore } from './_store/user-store'
import { UserTeamsListProvider } from '~/hooks-data/team/use-user-teams-list'
import { DashboardSlugsSetUp } from '~/hooks/use-dashboard-slugs'
import Cookies from 'js-cookie'
import { makeFullNameFromClerkUser } from '@basehub/data/auth/utils/make-full-name-from-clerk-user'
import { ClientDebuggerProvider } from '~/hooks/use-client-debugger'

export const Providers = ({ children }: { children?: React.ReactNode }) => {
  const { optimisticUser, isSyncing } = useUserStore()

  // save optimisticUser to a cookie so we can consume it in the help center
  React.useEffect(() => {
    if (optimisticUser && !isSyncing) {
      Cookies.set(
        'intercom-user',
        JSON.stringify({
          name: makeFullNameFromClerkUser(optimisticUser),
          email: optimisticUser.email ?? undefined,
          imageUrl: optimisticUser.imageUrl ?? undefined,
          id: optimisticUser.id,
        }),
        {
          domain: '.basehub.com',
          expires: 365,
          path: '/',
        }
      )
    }
  }, [isSyncing, optimisticUser])

  return (
    <ClientDebuggerProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider disableHoverableContent>
          <UserStoreSetUp />
          <UserTeamsListProvider>
            <NuqsAdapter>{children}</NuqsAdapter>
          </UserTeamsListProvider>
          <React.Suspense fallback={null}>
            <Toaster />
            <IsNavigatingWithKeyboardSetUp />
            <DashboardSlugsSetUp />
          </React.Suspense>
        </TooltipProvider>
      </QueryClientProvider>
    </ClientDebuggerProvider>
  )
}

/* -------------------------------------------------------------------------------------------------
 * Is Navigating With Keyboard
 * -----------------------------------------------------------------------------------------------*/

export const useIsNavigatingWithKeyboard = create<{
  isNavigatingWithKeyboard: boolean
  setIsNavigatingWithKeyboard: (isNavigatingWithKeyboard: boolean) => void
}>((set) => {
  return {
    isNavigatingWithKeyboard: false,
    setIsNavigatingWithKeyboard: (isNavigatingWithKeyboard: boolean) =>
      set({ isNavigatingWithKeyboard }),
  }
})

export function isKeyValidForNavigation(key: string) {
  return ['Tab', 'ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'].includes(
    key
  )
}

const IsNavigatingWithKeyboardSetUp = () => {
  const { setIsNavigatingWithKeyboard } = useIsNavigatingWithKeyboard()

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isKeyValidForNavigation(event.key)) {
        setIsNavigatingWithKeyboard(true)
      }
    }

    const handleMouseDown = () => {
      setIsNavigatingWithKeyboard(false)
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('mousedown', handleMouseDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('mousedown', handleMouseDown)
    }
  })

  return <></>
}
